import { resolveTotalsBlockName } from "../totalsBlockName";

import type { FinancialsTableResponse, TotalsBlock } from "@platform/api";

const getVisibleCoverageIds = (
  payload: FinancialsTableResponse,
): ReadonlySet<string> =>
  new Set(payload.coverages.filter((c) => !c.hidden).map((c) => c.id));

const intersect = (
  a: ReadonlySet<string>,
  b: ReadonlySet<string>,
): ReadonlySet<string> => new Set([...a].filter((id) => b.has(id)));

const subtract = (
  from: ReadonlySet<string>,
  remove: ReadonlySet<string>,
): ReadonlySet<string> => new Set([...from].filter((id) => !remove.has(id)));

/**
 * Precomputes the set of visible coverage IDs that belong to each
 * totals block. Coverages not listed in any block are omitted from
 * all aggregation.
 */
export const buildCoverageIdsByBlock = (
  payload: FinancialsTableResponse,
): ReadonlyMap<string, ReadonlySet<string>> => {
  const visible = getVisibleCoverageIds(payload);

  return new Map(
    payload.totalsBlocks.map((block) => [
      block.id,
      intersect(new Set(block.coverages.map((c) => c.coverageId)), visible),
    ]),
  );
};

/**
 * Maps each coverage ID to the display name of the block it
 * belongs to. Coverages not in any block are not included.
 */
export const buildCoverageIdToBlockName = (
  blocks: TotalsBlock[],
): ReadonlyMap<string, readonly string[]> => {
  const map = new Map<string, string[]>();

  for (const block of blocks) {
    const name = resolveTotalsBlockName(block);

    for (const c of block.coverages) {
      const existing = map.get(c.coverageId);

      if (existing) {
        existing.push(name);
      } else {
        map.set(c.coverageId, [name]);
      }
    }
  }

  return map;
};

/**
 * Visible coverage IDs not present in any block's coverage list.
 */
export const buildOrphanedCoverageIds = (
  payload: FinancialsTableResponse,
): ReadonlySet<string> => {
  const visible = getVisibleCoverageIds(payload);
  const claimed = new Set(
    payload.totalsBlocks.flatMap((b) => b.coverages.map((c) => c.coverageId)),
  );

  return subtract(visible, claimed);
};

/**
 * Maps block ID to its `TotalsBlock` for label resolution and
 * other lookups.
 */
export const buildBlockById = (
  blocks: TotalsBlock[],
): ReadonlyMap<string, TotalsBlock> => new Map(blocks.map((b) => [b.id, b]));
